import { createFriendMarkers } from "./friends"
import { friendsPillState } from "./map_control_state"
import { createPresencePoller } from "./presence"
import { friendTracksAccumulate } from "./track_lines_policy"
import { esc } from "./html"
import { respondToInvite, tripInvitesPillState } from "./trip_invites"

// Owns the web map's social-presence lifecycle: polling, friend markers and
// breadcrumb accumulation, plus the presence-fed invite controls. The map
// Stimulus controller supplies only current tracking/location inputs and a
// render callback for the shared GeoJSON friend-track source.
export class PresenceCoordinator {
  constructor({
    map,
    maplibregl,
    url,
    tripInvitesUrl,
    heartbeatBody,
    intervalSec,
    friendsPill = null,
    invitesPill = null,
    getLastLocation = () => null,
    autoTrackFriends = () => false,
    onFriendTracks = () => {},
    onTripStateTerminal = () => {},
    setStatus = () => {},
    pollerFactory = createPresencePoller,
    markerFactory = createFriendMarkers,
    inviteResponder = respondToInvite
  }) {
    this.map = map
    this.maplibregl = maplibregl
    this.url = url
    this.tripInvitesUrl = tripInvitesUrl
    this.heartbeatBody = heartbeatBody
    this.intervalSec = intervalSec
    this.friendsPill = friendsPill
    this.invitesPill = invitesPill
    this.getLastLocation = getLastLocation
    this.autoTrackFriends = autoTrackFriends
    this.onFriendTracks = onFriendTracks
    this.onTripStateTerminal = onTripStateTerminal
    this.setStatus = setStatus
    this.pollerFactory = pollerFactory
    this.markerFactory = markerFactory
    this.inviteResponder = inviteResponder

    this.friends = []
    this.invites = []
    this.friendTracks = {}
    this.dismissedInvites = new Set()
    this.inviteBusyId = null
    this.inviteError = null
  }

  start() {
    if (this.poller) return
    this.friendMarkers = this.markerFactory(this.map, this.maplibregl)
    this.poller = this.pollerFactory({
      url: this.url,
      body: this.heartbeatBody,
      intervalSec: this.intervalSec,
      onData: data => this.#onPresence(data),
      onStale: () => this.#onPresence({ friends: [] }),
      onTripStateTerminal: () => this.onTripStateTerminal()
    })
  }

  stop() {
    this.poller?.stop()
    this.poller = null
    this.friendMarkers?.destroy()
    this.friendMarkers = null
  }

  kick() {
    this.poller?.kick()
  }

  settingChanged(key) {
    if (key !== "autoTrackFriends" || this.autoTrackFriends()) return
    if (Object.keys(this.friendTracks).length) this.friendTracks = {}
    this.onFriendTracks(this.friendTracks)
  }

  async acceptInvite(event) {
    await this.#respondToInvite(event, "accepted")
  }

  async declineInvite(event) {
    await this.#respondToInvite(event, "declined")
  }

  #onPresence({ friends = [], invites = [] } = {}) {
    this.friends = friends
    this.invites = invites
    this.friendMarkers?.update(friends)
    this.#renderFriendsPill()
    this.#renderInvitesPill()

    if (this.autoTrackFriends()) {
      this.friendTracks = friendTracksAccumulate(this.friendTracks, friends)
    } else if (Object.keys(this.friendTracks).length) {
      this.friendTracks = {}
    }
    this.onFriendTracks(this.friendTracks)
  }

  async #respondToInvite(event, status) {
    const id = event.currentTarget?.dataset?.inviteId
    if (id == null || this.inviteBusyId != null || !this.tripInvitesUrl) return
    this.inviteBusyId = id
    this.inviteError = null
    this.#renderInvitesPill()
    try {
      const last = this.getLastLocation()
      await this.inviteResponder({
        baseUrl: this.tripInvitesUrl,
        id,
        status,
        lat: last?.[0],
        lon: last?.[1]
      })
      this.dismissedInvites.add(id)
      this.dismissedInvites.add(Number(id))
      this.invites = this.invites.filter(invite => String(invite.id) !== String(id))
      if (status === "accepted") this.setStatus("Invite accepted — start tracking to join the trip.")
    } catch (error) {
      this.inviteError = error?.message || "Couldn't answer the invite."
    } finally {
      this.inviteBusyId = null
      this.#renderInvitesPill()
    }
  }

  #renderFriendsPill() {
    if (!this.friendsPill) return
    const state = friendsPillState(this.friends)
    this.friendsPill.classList.toggle("hidden", state.hidden)
    if (!state.hidden) this.friendsPill.textContent = state.text
  }

  #renderInvitesPill() {
    if (!this.invitesPill) return
    const state = tripInvitesPillState(this.invites, {
      dismissedIds: this.dismissedInvites,
      busyId: this.inviteBusyId,
      error: this.inviteError
    })
    const signature = JSON.stringify(state)
    if (signature === this.invitesRenderSignature) return
    this.invitesRenderSignature = signature
    this.invitesPill.classList.toggle("hidden", state.hidden)
    this.invitesPill.innerHTML = state.invites.map(invite => `
      <div class="flex items-center gap-2">
        <span class="text-sm font-semibold text-slate-800">${esc(invite.label)}</span>
        <button type="button" data-action="map#acceptInvite" data-invite-id="${esc(invite.id)}" ${invite.busy ? "disabled" : ""}
                class="rounded-full bg-blue-600 px-3 py-1 text-xs font-extrabold text-white hover:bg-blue-700 disabled:opacity-50">Join</button>
        <button type="button" data-action="map#declineInvite" data-invite-id="${esc(invite.id)}" ${invite.busy ? "disabled" : ""}
                aria-label="Decline invite"
                class="flex h-6 w-6 items-center justify-center rounded-full text-lg leading-none text-slate-400 hover:bg-slate-100 disabled:opacity-50">×</button>
      </div>
      ${invite.error ? `<div class="text-xs font-semibold text-red-600">${esc(invite.error)}</div>` : ""}
    `).join("")
  }
}
